// @flow

import _ from 'lodash';
import React, { useState, useCallback, useEffect } from 'react';
import Tour from 'reactour';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { matchPath } from 'react-router';
import { useLocation } from 'react-router-dom';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import { Icon, Popover, Badge } from '@shopify/polaris';
import classNames from 'classnames';

import history from 'src/routes/history';
import { ADMIN_APP_ASSET_PATHS } from 'src/constants/assets';
import { getAssetURL } from 'src/helpers';
import { ComposedPropsItem } from './index.type';
import { EXTRA_ACTION } from 'src/helpers/menu-helper';
import { routes } from 'src/routes';
import * as themeIntegrationAction from 'actions/shopify-integration/theme-integration-actions';
import { CONFIG_AB_TESTING_TOUR } from 'src/constants/ab-testing';

const MainMenuItem: React.FC<ComposedPropsItem> = ({
  menuId,
  menuIdMetaMatchOldNav,
  position = 'left',
  data,
  i18n,
  className,
  type,
  updateFieldReducer,
  skipTourABTesting,
  updateFeaturesAppSettings,
  tenantApp
}) => {
  const [popoverActive, setPopoverAction] = useState(false);
  const [openTour, setOpenTour] = useState(false);
  const { id, title, icon, routeTo, children, isNew: isMainNew, isMenuOneLevel, isNewStyle } = data;
  const skipTourGuide = _.get(tenantApp, 'settings.abTesting.settings.skip-tour-guide', false);
  const [tourKey, setTourKey] = useState(0);

  const hasSubMenu = _.isEmpty(routeTo);
  let content = null;

  const togglePopoverActive = useCallback(() => {
    setPopoverAction(prevState => !prevState);
  }, []);

  const location = useLocation();

  const handleExtraAction = (action: string) => {
    switch (action) {
      case EXTRA_ACTION.CLEAR_CACHE_MANAGE_TEMPLATE:
        if (
          matchPath(routes.shopifyIntegration.manageTemplateInstance(), {
            path: location.pathname,
            exact: true,
            strict: false
          })
        ) {
          return;
        }
        updateFieldReducer('selectedThemeId', '');
        break;
      default:
        break;
    }
  };

  // Disable/enable scroll in product tour
  const disableBody = target => {
    disableBodyScroll(target);
  };
  const enableBody = target => enableBodyScroll(target);

  useEffect(() => {
    if (openTour) {
      const el = document.querySelector('[data-tour="pfs-introduce-ab-testing"]');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });

        // Delay re-opening the tour for accurate positioning
        setTimeout(() => {
          setTourKey('pfs-introduce-ab-testing-tour'); // remount Tour to force recalculation
        }, 300);
      }
    } else {
      const el = document.getElementsByClassName('ab-product-tour-step0');
      enableBody(el);
    }
  }, [openTour]);

  //close tour
  const closeTour = path => {
    setOpenTour(false);
    const meta = {
      onSuccess: () => {
        updateFeaturesAppSettings({ 'skip-tour-guide': true }, 'abTesting');
      }
    };
    skipTourABTesting(meta);
    history.push(path);
  };

  if (hasSubMenu) {
    const activator = (
      <span className={'main-item'}>
        <Icon source={icon} />
        {title}
        {isMainNew && (
          <div style={{ marginLeft: '4px' }}>
            <Badge status="info">
              <span>{i18n.translate('AdminHTML.MainMenuV2.Common.new')}</span>
            </Badge>
          </div>
        )}
      </span>
    );

    const onAction = (route: string, extraAction: string) => {
      if (extraAction) {
        handleExtraAction(extraAction);
      }
      if (
        matchPath(routes.shopifyIntegration.manageTemplateInstance(), {
          path: location.pathname,
          exact: true,
          strict: false
        }) &&
        route === routes.shopifyIntegration.templateLibrary()
      ) {
        return;
      }
      setPopoverAction(false);

      if (location.pathname !== route) {
        history.push(route);
      }
    };

    const actionList = isMenuOneLevel ? (
      <div
        className={classNames('menu-actions menu-actions--one-level Polaris-ActionList__Actions')}>
        {children
          .map(({ title, id, isHide, routeTo }) => {
            if (isHide) return null;
            return (
              <div id={id} key={id} className="group">
                <div
                  className={`group-element group-element--one-level Polaris-ActionList__Item ${
                    id === menuId || menuIdMetaMatchOldNav?.indexOf(id) > -1
                      ? 'Polaris-ActionList--active'
                      : ''
                  }`}
                  onClick={() => onAction(routeTo)}>
                  {/* <Icon source={icon} /> */}
                  {title}
                </div>
              </div>
            );
          })
          .filter(e => e)}
      </div>
    ) : (
      <div
        className={classNames('menu-actions Polaris-ActionList__Actions', {
          'menu-actions--new-style': isNewStyle
        })}>
        {children
          .map(({ title, id, icon, subMenu = [], isHide }) => {
            if (isHide) return null;
            return (
              <div id={id} key={id} className="group">
                <div className="group-title">
                  {icon && <Icon source={icon} />}
                  {title}
                </div>
                {subMenu
                  .filter(sub => !sub.isHide)
                  .map(({ title, isNew, isBeta, id, routeTo, badge, extraAction }) => (
                    <div
                      className={classNames('group-element Polaris-ActionList__Item', {
                        'Polaris-ActionList--active':
                          id === menuId || menuIdMetaMatchOldNav?.indexOf(id) > -1,
                        'group-element--new-style': isNewStyle
                      })}
                      onClick={() => onAction(routeTo, extraAction)}
                      id={id}
                      key={id}>
                      {title}
                      {badge === 'ai' && (
                        <div className="badge-ai">
                          <img
                            width={12}
                            height={12}
                            src={getAssetURL(ADMIN_APP_ASSET_PATHS?.subscriptionV4?.star)}
                            alt="wand"
                          />
                        </div>
                      )}
                      {isNew && (
                        <div style={{ marginLeft: '6px' }}>
                          <Badge status="info">
                            <span>{i18n.translate('AdminHTML.MainMenuV2.Common.new')}</span>
                          </Badge>
                        </div>
                      )}
                      {isBeta && (
                        <div style={{ marginLeft: '6px' }}>
                          <Badge>
                            <span>{i18n.translate('AdminHTML.Common.Badge.beta')}</span>
                          </Badge>
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            );
          })
          .filter(e => e)}
      </div>
    );

    content = (
      <Popover
        active={popoverActive}
        activator={activator}
        onClose={() => {}}
        preferredAlignment={position}>
        {actionList}
      </Popover>
    );
  } else {
    content = (
      <span
        onClick={() => {
          if (location.pathname !== routeTo) {
            if (id === 'menu-merchandising-ab-testing' && type !== 'mobile' && !skipTourGuide) {
              setOpenTour(true);
            } else history.push(routeTo);
          }
        }}
        className={'main-item'}>
        <Icon source={icon} />
        {title}
        {isMainNew && (
          <div style={{ marginLeft: '4px' }}>
            <Badge status="info">
              <span>{i18n.translate('AdminHTML.MainMenuV2.Common.new')}</span>
            </Badge>
          </div>
        )}
      </span>
    );
  }

  const events = hasSubMenu
    ? {
        onMouseEnter: togglePopoverActive,
        onMouseLeave: togglePopoverActive
      }
    : {};
  if (id === 'menu-merchandising-ab-testing' && type !== 'mobile') {
    return (
      <div
        data-tour="pfs-introduce-ab-testing"
        key={id}
        {...events}
        className={`menuItem ${id} ${menuId.indexOf(id) > -1 ? 'active' : ''} ${className ?? ''} ${
          popoverActive ? 'hovered' : ''
        }`}>
        {content}

        {/* Tour */}
        <div className="BC-Component__SearchGuideTour">
          <Tour
            key={tourKey} //
            showNumber={false}
            showButtons={false}
            showNavigation={false}
            closeWithMask={false}
            showCloseButton={false}
            startAt={0}
            rounded={8}
            maskSpace={10}
            aria-label={'Close'}
            // Render steps tour
            steps={CONFIG_AB_TESTING_TOUR(i18n, () => {
              closeTour(routeTo);
            })}
            isOpen={openTour}
            className={`ab-product-tour-step0`}
            accentColor="#003D2C"
            onAfterOpen={disableBody}
            onBeforeClose={enableBody}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      key={id}
      {...events}
      className={`menuItem ${id} ${menuId.indexOf(id) > -1 ? 'active' : ''} ${className ?? ''} ${
        popoverActive ? 'hovered' : ''
      }`}>
      {content}
    </div>
  );
};

const mapStateToProps = state => {
  return {
    host: _.get(state.oauth, 'host', '')
  };
};

const mapDispatchToProps = dispatch => ({
  updateFieldReducer: (fieldName, data) =>
    dispatch(themeIntegrationAction.handleUpdateField(fieldName, data))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(MainMenuItem));
